// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import Tag from "@insite/mobius/Icons/Tag";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button, { ButtonPresentationProps } from "@plumberssupply/MobiusOverrides/Button/Button";
import WishListSharingStatus, {
    WishListSharingStatusStyles,
} from "@plumberssupply/Overrides/Components/WishListSharingStatus";
import Close from "@plumberssupply/XDL/Icons/Close";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Eye from "@insite/mobius/Icons/Eye";
import IconSignOut from "@plumberssupply/XDL/Icons/IconSignOut";
import { color, ellipsis, mergeWithCss, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { useContext } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
    // XNG-Changes
    cartPageLink: getPageLinkByPageType(state, "CartPage")?.url,
});

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    hideTotalSection?: boolean; // XNG-Changes
    addWishListToCart: HandleThunkActionCreator<typeof addWishListToCart>;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
    toggleFavorite?: () => void;
    toggleHide?: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface WishListCardStyles {
    gridContainer?: GridContainerProps;
    wishListInfoGridItem?: GridItemProps;
    wishListInfoGridContainer?: GridContainerProps;
    basicInfoGridItem?: GridItemProps;
    basicInfoGridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyProps;
    lastUpdatedGridItem?: GridItemProps;
    lastUpdatedText?: TypographyProps;
    extendedInfoGridItem?: GridItemProps;
    extendedInfoGridContainer?: GridContainerProps;
    productImagesGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    statusGridItem?: GridItemProps;
    sharingStatus?: WishListSharingStatusStyles;
    actionGridItem?: GridItemProps;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowIcon?: IconPresentationProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionHideClickable?: ClickablePresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionLeaveClickable?: ClickablePresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    actionHideLink?: LinkPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionLeaveButton?: ButtonPresentationProps;
    actionFavoriteClickable?: ClickablePresentationProps;
    basicIconGridContainer?: GridContainerProps;
    detailIconGridItem?: GridItemProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    tagTooltip?: TooltipPresentationProps;
    // XNG-Changes
    wishListNameStyles?: InjectableCss;
    totalGridItem?: GridItemProps;
    statusWrapper?: InjectableCss;
    realTimeText?: TypographyPresentationProps;
    productImagesMoreLink?: LinkPresentationProps;
}

// XNG-Changes: style
export const wishListCardStyles: WishListCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            ${xScreen("lte", "md")} {
                height: 100%;
                & > ${GridOffset} {
                    height: 100%;
                }
            }
            ${xScreen("lte", "sm")} {
                height: auto;
            }
        `,
    },
    wishListInfoGridItem: {
        width: [12, 12, 12, 10, 10],
        align: "middle",
    },
    wishListInfoGridContainer: {
        gap: 0,
        css: css`
            height: 100%;
        `,
    },
    basicInfoGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            align-items: baseline;
            gap: 5px;
        `,
    },
    basicInfoGridContainer: {
        gap: 0,
        css: css`
            padding-right: 22px;
            ${xScreen("lte", "md")} {
                padding-right: 0;
            }
        `,
    },
    detailLinkGridItem: { width: 12 },
    descriptionGridItem: { width: 12 },
    descriptionText: {
        ellipsis: true,
        css: css`
            margin-top: 12px;
        `,
    },
    lastUpdatedGridItem: { width: 12 },
    extendedInfoGridItem: {
        width: [12, 12, 12, 8, 8],
        align: "middle",
    },
    extendedInfoGridContainer: {
        gap: 0,
    },
    productImageLink: {
        css: css`
            margin-right: 12px;
            ${TypographyStyle} {
                font-size: 14px;
                line-height: 16px;
            }
        `,
    },
    productImagesGridItem: {
        align: "middle",
        css: css`
            flex-wrap: wrap;
            ${xScreen("lte", "md")} {
                flex-basis: 100%;
                max-width: 100%;
                &&& {
                    margin-top: 20px;
                }
            }

            [data-test-selector="productImagesMoreLink"] {
                ${TypographyStyle} {
                    font-size: 14px;
                    line-height: 22px;
                    font-weight: 700;
                }
            }
        `,
    },
    productImage: {
        css: css`
            height: 64px;
            width: 64px;
            img {
                height: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
        errorTypographyProps: {
            size: 11,
            css: css`
                max-height: 65px;
            `,
        },
    },
    statusGridItem: {
        width: [12, 12, 12, 3, 3],
        align: "middle",
        css: css`
            ${xScreen("lte", "md")} {
                &&& {
                    margin-top: 20px;
                }
            }
        `,
    },
    statusWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 8px;
        `,
    },
    actionGridItem: {
        width: [12, 12, 12, 2, 2],
        css: css`
            align-items: flex-end;
            justify-content: flex-start;
            flex-direction: column;

            ${xScreen("lte", "md")} {
                &&& {
                    flex-direction: row;
                    justify-content: space-between;
                    margin-top: 20px;
                }
            }
        `,
    },
    actionGridMediumHidden: {
        css: css`
            width: 100%;
        `,
    },
    actionAddToCartButton: {
        sizeVariant: "small",
        color: "secondary",
        css: css`
            white-space: nowrap;
            padding: 0 10px;
            width: 118px;
        `, // keep text from wrapping
    },
    actionHideLink: {
        css: css`
            display: block;
            margin: 10px auto 0 auto;
        `,
    },
    actionDeleteButton: {
        sizeVariant: "small",
        variant: "tertiary",
        css: css`
            margin-top: 10px;
            width: 118px;
            display: flex;
            justify-content: center;
            align-items: center;
            ${TypographyStyle} {
                font-size: 10px;
                line-height: 13px;
            }

            ${IconWrapper} {
                opacity: 0.5;
                top: 0;
                margin-right: 8px;
                svg {
                    height: 10px;
                    width: 7px;
                }
            }
            ${xScreen("lte", "md")} {
                margin-top: 0;
                padding: 0;
                width: 80px;
            }
        `,
    },
    actionLeaveButton: {
        sizeVariant: "small",
        variant: "tertiary",
        css: css`
            margin-top: 10px;
            width: 118px;
            ${IconWrapper} {
                opacity: 0.5;
                svg {
                    height: 10px;
                    width: 10px;
                }
            }

            ${xScreen("lte", "md")} {
                margin-top: 0;
                padding: 0;
                width: 80px;
            }
        `,
    },
    wishListNameStyles: {
        css: css`
            &&&& span {
                color: ${color("common.ui11")};
                font-size: 18px;
                line-height: 22px;
                font-weight: 800;
                ${ellipsis(2)}
            }
        `,
    },
    lastUpdatedText: {
        css: css`
            font-size: 14px;
            margin-top: 12px;
            line-height: 22px;

            span {
                font-weight: 700;
            }
        `,
    },
    // XNG-Changes
    totalGridItem: {
        width: [12, 12, 12, 3, 3],
        align: "middle",
        css: css`
            ${TypographyStyle} {
                margin-bottom: 0;
            }

            ${xScreen("lte", "md")} {
                &&& {
                    margin-top: 20px;
                }
            }
        `,
    },
    realTimeText: {
        size: 12,
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    basicIconGridContainer: {
        gap: 10,
        css: css`
            width: 15px;
            flex-shrink: 0;
        `,
        offsetCss: css`
            flex-direction: column;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
        css: css`
            svg path {
                fill: ${color("primary")};
            }
        `,
    },
    tagTooltip: {
        iconProps: {
            src: Tag,
            size: 15,
            css: css`
                margin: 4px 0;
            `,
        },
    },
};

const WishListCard = ({
    language,
    allowEditingOfWishLists,
    isUpdatingCart,
    extendedStyles,
    wishList,
    hideTotalSection,
    addWishListToCart,
    deleteWishList,
    leaveWishList,
    toggleFavorite,
    toggleHide,
    cartPageLink,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const isLoadingAnyList = useAppSelector(state => Object.keys(state.data.wishLists.isLoading).length > 0);

    const history = useHistory();

    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        addWishListToCart({
            apiParameter: {
                wishListId: wishList.id,
            },
            ...{
                jobName: wishList.properties.plumbers_jobName,
                isSingleUse: wishList.properties.plumbers_isSingleUse,
                isSaved: wishList.properties.plumbers_isSaved,
            },
            onSuccess: onAddAllToCartSuccess,
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("List Added to Cart"),
            messageType: "success",
        });

        // XNG-Changes
        history.push(cartPageLink!);
    };

    const updatedOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishList.updatedOn),
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    const canAddToCart = wishList.canAddToCart && !!wishList.wishListLinesCount && wishList.wishListLinesCount > 0;
    const addToCartLabel = translate("Add List to Cart");
    const toggleHideButtonText = wishList.isHidden ? translate("Unhide List") : translate("Hide List");
    const deleteLabel = translate("Delete List");
    const leaveLabel = translate("Leave List");
    const [styles] = React.useState(() => mergeWithCss(wishListCardStyles, extendedStyles));

    return (
        <GridContainer {...styles.gridContainer} data-test-selector="wishListCard">
            <GridItem {...styles.wishListInfoGridItem} data-test-selector={`${wishList.name}`}>
                <GridContainer {...styles.wishListInfoGridContainer}>
                    <GridItem {...styles.basicInfoGridItem}>
                        <GridContainer {...styles.basicIconGridContainer}>
                            {!wishList.isAutogenerated && (
                                <GridItem {...styles.detailIconGridItem}>
                                    <Clickable
                                        {...styles.actionFavoriteClickable}
                                        onClick={toggleFavorite}
                                        data-test-selector={`myListsFavorite_${wishList.id}`}
                                        disabled={isLoadingAnyList}
                                    >
                                        {wishList.isFavorite && (
                                            <Icon
                                                {...styles.starXIcon}
                                                data-test-selector={`myListsFavoriteStarXIcon_${wishList.id}`}
                                            />
                                        )}
                                        {!wishList.isFavorite && (
                                            <Icon
                                                {...styles.starIcon}
                                                data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`}
                                            />
                                        )}
                                    </Clickable>
                                </GridItem>
                            )}
                            {wishList.wishListTags && wishList.wishListTags.length > 0 && (
                                <GridItem
                                    {...styles.detailIconGridItem}
                                    data-test-selector={`myListsTags_${wishList.id}`}
                                >
                                    <Tooltip
                                        {...styles.tagTooltip}
                                        text={`${translate("Tags Assigned")}: ${wishList.wishListTags
                                            .map(o => o.tag)
                                            .join(", ")}`}
                                    />
                                </GridItem>
                            )}
                        </GridContainer>
                        <GridContainer {...styles.basicInfoGridContainer}>
                            <GridItem {...styles.detailLinkGridItem}>
                                <Typography {...styles.wishListNameStyles}>
                                    <MyListsDetailsPageTypeLink
                                        title={wishList.name}
                                        wishListId={wishList.id}
                                        testSelector="wishListCardName"
                                    />
                                </Typography>
                            </GridItem>
                            {wishList.description && (
                                <GridItem {...styles.descriptionGridItem}>
                                    <Typography
                                        {...styles.descriptionText}
                                        data-test-selector="wishListCardDescription"
                                    >
                                        {wishList.description}
                                    </Typography>
                                </GridItem>
                            )}
                            <GridItem {...styles.lastUpdatedGridItem} data-test-selector="wishListCardLastUpdated">
                                <Typography {...styles.lastUpdatedText}>
                                    {translate(" Updated ")}
                                    <Typography>{updatedOnDisplay}</Typography>
                                    {wishList.updatedByDisplayName && !wishList.isAutogenerated && translate(" by ")}
                                    {wishList.updatedByDisplayName && !wishList.isAutogenerated && (
                                        <Typography>{wishList.updatedByDisplayName}</Typography>
                                    )}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.extendedInfoGridItem}>
                        <GridContainer {...styles.extendedInfoGridContainer}>
                            <GridItem
                                {...styles.productImagesGridItem}
                                width={hideTotalSection === true ? [12, 12, 10, 9, 9] : [12, 12, 7, 6, 6]}
                                data-test-selector="productImagesGridItem"
                            >
                                {wishList.wishListLineCollection &&
                                    wishList.wishListLineCollection.length > 0 &&
                                    wishList.wishListLineCollection.slice(0, 3).map(line => (
                                        <Link
                                            key={line.id.toString()}
                                            {...styles.productImageLink}
                                            href={line.productUri}
                                        >
                                            <LazyImage
                                                {...styles.productImage}
                                                src={line.smallImagePath}
                                                altText={line.altText}
                                                title={line.altText}
                                            />
                                        </Link>
                                    ))}
                                {wishList.wishListLinesCount > 3 && (
                                    <MyListsDetailsPageTypeLink
                                        title={`+ ${wishList.wishListLinesCount - 3} ${translate("More") as string}`}
                                        wishListId={wishList.id}
                                        testSelector="productImagesMoreLink"
                                    />
                                )}
                            </GridItem>
                            <GridItem {...styles.statusGridItem}>
                                {/* XNG-Changes */}
                                <StyledWrapper {...styles.statusWrapper}>
                                    <WishListSharingStatus
                                        showSavedStatus={true}
                                        extendedStyles={styles.sharingStatus}
                                        wishList={wishList}
                                    />
                                </StyledWrapper>
                            </GridItem>
                            {hideTotalSection !== true && (
                                <GridItem {...styles.totalGridItem}>
                                    {parseFloat(wishList.properties.total) >= 0 ? (
                                        <Typography variant="h5" color="common.ui11">
                                            <LocalizedCurrency amount={parseFloat(wishList.properties.total)} />
                                        </Typography>
                                    ) : (
                                        <Typography {...styles.realTimeText} />
                                    )}
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <GridItem {...styles.actionGridItem}>
                <Button
                    {...styles.actionAddToCartButton}
                    disabled={!canAddToCart || isUpdatingCart}
                    data-test-selector={`wishListAddToCartButton_${wishList.id}`}
                    onClick={clickAddToCartHandler}
                >
                    {addToCartLabel}
                </Button>
                {!wishList.isAutogenerated && (
                    // XNG-Changes
                    <Button
                        {...styles.actionDeleteButton}
                        onClick={toggleHide}
                        data-test-selector={`myListsHide_${wishList.id}`}
                        disabled={isLoadingAnyList}
                        icon={{ src: wishList.isHidden ? EyeOff : Eye, position: "left", color: "secondary" }}
                    >
                        {toggleHideButtonText}
                    </Button>
                )}
                {allowEditingOfWishLists && !!deleteWishList && !wishList.isSharedList && (
                    <Button
                        {...styles.actionDeleteButton}
                        onClick={deleteWishList}
                        data-test-selector={`wishListCardDeleteButton_${wishList.id}`}
                        // XNG-Changes
                        icon={{ src: Close, position: "left", color: "secondary" }}
                    >
                        {deleteLabel}
                    </Button>
                )}
                {allowEditingOfWishLists &&
                    !!leaveWishList &&
                    wishList.isSharedList &&
                    wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                        <Button
                            {...styles.actionLeaveButton}
                            onClick={leaveWishList}
                            data-test-selector={`wishListCardLeaveButton_${wishList.id}`}
                            // XNG-Changes
                            icon={{ src: IconSignOut, position: "left", color: "secondary" }}
                        >
                            {leaveLabel}
                        </Button>
                    )}
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(WishListCard);
